import React, { useRef, useEffect, useMemo } from 'react';

/**
 * ToolpathViewer - 2D visualization of G-code toolpaths
 * Parses G-code and renders the toolpath on a canvas
 * 
 * Props:
 * - gcode: String of G-code to visualize
 * - width: Canvas width (default 400)
 * - height: Canvas height (default 300)
 * - machineType: 'lathe' | 'milling' (affects coordinate mapping)
 * - showRapids: Show rapid moves (G00) in different color
 * - showGrid: Show background grid
 */
const ToolpathViewer = ({
  gcode = '',
  width = 400,
  height = 300,
  machineType = 'milling',
  showRapids = true,
  showGrid = true,
  showWorkpiece = true,
  stockDimensions = { diameter: 2.0, length: 4.0, width: 4.0, height: 4.0 },
  className = '',
}) => {
  const canvasRef = useRef(null);
  
  // Parse G-code into toolpath segments
  const toolpath = useMemo(() => {
    if (!gcode) return { segments: [], bounds: null };
    
    const segments = [];
    let currentPos = { x: 0, y: 0, z: 0 };
    let isAbsolute = true;
    let isRapid = false;
    let isMetric = false;
    
    // Bounds tracking
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    
    const lines = gcode.split('\n');
    
    for (const line of lines) {
      const cleanLine = line.split('(')[0].split(';')[0].trim().toUpperCase();
      if (!cleanLine) continue;
      
      // Check for modal codes
      if (cleanLine.includes('G90')) isAbsolute = true;
      if (cleanLine.includes('G91')) isAbsolute = false;
      if (cleanLine.includes('G20')) isMetric = false;
      if (cleanLine.includes('G21')) isMetric = true;
      
      // Extract coordinates
      const xMatch = cleanLine.match(/X(-?\d*\.?\d+)/);
      const yMatch = cleanLine.match(/Y(-?\d*\.?\d+)/);
      const zMatch = cleanLine.match(/Z(-?\d*\.?\d+)/);
      const uMatch = cleanLine.match(/U(-?\d*\.?\d+)/); // Lathe incremental X
      const wMatch = cleanLine.match(/W(-?\d*\.?\d+)/); // Lathe incremental Z
      
      // Determine move type
      if (cleanLine.includes('G00') || cleanLine.includes('G0 ')) {
        isRapid = true;
      } else if (cleanLine.includes('G01') || cleanLine.includes('G1 ') ||
                 cleanLine.includes('G02') || cleanLine.includes('G2 ') ||
                 cleanLine.includes('G03') || cleanLine.includes('G3 ')) {
        isRapid = false;
      }
      
      // Skip if no coordinate change
      if (!xMatch && !yMatch && !zMatch && !uMatch && !wMatch) continue;
      
      const startPos = { ...currentPos };
      
      // Update position
      if (xMatch) {
        const val = parseFloat(xMatch[1]);
        currentPos.x = isAbsolute ? val : currentPos.x + val;
      }
      if (yMatch) {
        const val = parseFloat(yMatch[1]);
        currentPos.y = isAbsolute ? val : currentPos.y + val;
      }
      if (zMatch) {
        const val = parseFloat(zMatch[1]);
        currentPos.z = isAbsolute ? val : currentPos.z + val;
      }
      // Lathe U/W incremental
      if (uMatch) {
        currentPos.x += parseFloat(uMatch[1]);
      }
      if (wMatch) {
        currentPos.z += parseFloat(wMatch[1]);
      }
      
      // Detect arc moves
      let isArc = false;
      let arcDir = 0; // 2 = CW (G02), 3 = CCW (G03)
      let arcCenter = { i: 0, j: 0, k: 0 };
      
      if (cleanLine.includes('G02') || cleanLine.includes('G2 ')) {
        isArc = true;
        arcDir = 2;
      } else if (cleanLine.includes('G03') || cleanLine.includes('G3 ')) {
        isArc = true;
        arcDir = 3;
      }
      
      if (isArc) {
        const iMatch = cleanLine.match(/I(-?\d*\.?\d+)/);
        const jMatch = cleanLine.match(/J(-?\d*\.?\d+)/);
        const kMatch = cleanLine.match(/K(-?\d*\.?\d+)/);
        if (iMatch) arcCenter.i = parseFloat(iMatch[1]);
        if (jMatch) arcCenter.j = parseFloat(jMatch[1]);
        if (kMatch) arcCenter.k = parseFloat(kMatch[1]);
      }
      
      // Add segment
      segments.push({
        start: startPos,
        end: { ...currentPos },
        isRapid,
        isArc,
        arcDir,
        arcCenter,
      });
      
      // Update bounds
      minX = Math.min(minX, startPos.x, currentPos.x);
      maxX = Math.max(maxX, startPos.x, currentPos.x);
      minY = Math.min(minY, startPos.y, currentPos.y);
      maxY = Math.max(maxY, startPos.y, currentPos.y);
      minZ = Math.min(minZ, startPos.z, currentPos.z);
      maxZ = Math.max(maxZ, startPos.z, currentPos.z);
    }
    
    const bounds = segments.length > 0 ? {
      minX, maxX, minY, maxY, minZ, maxZ,
      width: maxX - minX,
      height: maxY - minY,
      depth: maxZ - minZ,
    } : null;
    
    return { segments, bounds };
  }, [gcode]);
  
  // Draw the toolpath
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const { segments, bounds } = toolpath;
    
    // Clear canvas
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, width, height);
    
    if (!bounds || segments.length === 0) {
      // Draw "No toolpath" message
      ctx.fillStyle = '#666';
      ctx.font = '14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('No toolpath to display', width / 2, height / 2);
      return;
    }
    
    // Calculate scale and offset
    const padding = 30;
    const viewWidth = width - padding * 2;
    const viewHeight = height - padding * 2;
    
    // For lathe: X is radial (vertical), Z is axial (horizontal)
    // For milling: X is horizontal, Y is vertical
    let scaleX, scaleY, offsetX, offsetY;
    
    if (machineType === 'lathe') {
      // Lathe: Z horizontal, X vertical (mirrored for top/bottom)
      const rangeZ = bounds.maxZ - bounds.minZ || 1;
      const rangeX = bounds.maxX - bounds.minX || 1;
      scaleX = viewWidth / rangeZ;
      scaleY = viewHeight / (rangeX * 2); // Double for diameter view
      const scale = Math.min(scaleX, scaleY);
      scaleX = scaleY = scale;
      offsetX = padding - bounds.minZ * scaleX;
      offsetY = height / 2;
    } else {
      // Milling: X horizontal, Y vertical
      const rangeX = bounds.maxX - bounds.minX || 1;
      const rangeY = bounds.maxY - bounds.minY || 1;
      scaleX = viewWidth / rangeX;
      scaleY = viewHeight / rangeY;
      const scale = Math.min(scaleX, scaleY);
      scaleX = scaleY = scale;
      offsetX = padding + (viewWidth - rangeX * scaleX) / 2 - bounds.minX * scaleX;
      offsetY = padding + (viewHeight - rangeY * scaleY) / 2 + bounds.maxY * scaleY;
    }
    
    // Transform functions
    const toCanvasX = (machineType === 'lathe') 
      ? (z) => offsetX + z * scaleX 
      : (x) => offsetX + x * scaleX;
    const toCanvasY = (machineType === 'lathe')
      ? (x) => offsetY - x * scaleY
      : (y) => offsetY - y * scaleY;
    
    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 0.5;
      
      // Calculate grid spacing
      const gridSize = machineType === 'lathe' ? 0.5 : 1.0; // inches
      
      ctx.beginPath();
      // Vertical lines
      for (let x = Math.floor(bounds.minX); x <= Math.ceil(bounds.maxX); x += gridSize) {
        const cx = machineType === 'lathe' ? toCanvasX(x) : toCanvasX(x);
        if (machineType === 'lathe') {
          const cy = toCanvasY(x);
          ctx.moveTo(padding, cy);
          ctx.lineTo(width - padding, cy);
        } else {
          ctx.moveTo(cx, padding);
          ctx.lineTo(cx, height - padding);
        }
      }
      // Horizontal lines
      const rangeForHoriz = machineType === 'lathe' ? [bounds.minZ, bounds.maxZ] : [bounds.minY, bounds.maxY];
      for (let y = Math.floor(rangeForHoriz[0]); y <= Math.ceil(rangeForHoriz[1]); y += gridSize) {
        const cy = machineType === 'lathe' ? toCanvasX(y) : toCanvasY(y);
        if (machineType === 'lathe') {
          ctx.moveTo(cy, padding);
          ctx.lineTo(cy, height - padding);
        } else {
          ctx.moveTo(padding, cy);
          ctx.lineTo(width - padding, cy);
        }
      }
      ctx.stroke();
    }
    
    // Draw workpiece outline (for lathe)
    if (showWorkpiece && machineType === 'lathe') {
      const stockR = stockDimensions.diameter / 2;
      const stockL = stockDimensions.length;
      
      ctx.fillStyle = 'rgba(100, 100, 100, 0.3)';
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 1;
      
      // Draw stock as rectangle (Z horizontal, X vertical)
      const x1 = toCanvasX(0);
      const x2 = toCanvasX(stockL);
      const y1 = toCanvasY(stockR);
      const y2 = toCanvasY(-stockR);
      
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
    }
    
    // Draw workpiece for milling
    if (showWorkpiece && machineType === 'milling') {
      const stockW = stockDimensions.width || stockDimensions.length;
      const stockH = stockDimensions.height || stockDimensions.diameter;
      
      ctx.fillStyle = 'rgba(100, 100, 100, 0.3)';
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 1;
      
      const x1 = toCanvasX(0);
      const y1 = toCanvasY(stockH);
      const w = stockW * scaleX;
      const h = stockH * scaleY;
      
      ctx.fillRect(x1, y1, w, h);
      ctx.strokeRect(x1, y1, w, h);
    }
    
    // Draw toolpath segments
    for (const seg of segments) {
      ctx.beginPath();
      ctx.lineWidth = seg.isRapid ? 0.5 : 1.5;
      
      if (seg.isRapid) {
        ctx.strokeStyle = showRapids ? '#ff6600' : 'transparent';
        ctx.setLineDash([3, 3]);
      } else {
        ctx.strokeStyle = '#00ff00';
        ctx.setLineDash([]);
      }
      
      if (machineType === 'lathe') {
        // Lathe: Z=horizontal, X=vertical (draw both top and bottom profile)
        const z1 = toCanvasX(seg.start.z);
        const z2 = toCanvasX(seg.end.z);
        const x1 = toCanvasY(seg.start.x);
        const x2 = toCanvasY(seg.end.x);
        
        // Top profile
        ctx.moveTo(z1, x1);
        ctx.lineTo(z2, x2);
        
        // Bottom profile (mirrored)
        ctx.moveTo(z1, offsetY + (offsetY - x1));
        ctx.lineTo(z2, offsetY + (offsetY - x2));
      } else {
        // Milling: X=horizontal, Y=vertical
        const x1 = toCanvasX(seg.start.x);
        const y1 = toCanvasY(seg.start.y);
        const x2 = toCanvasX(seg.end.x);
        const y2 = toCanvasY(seg.end.y);
        
        if (seg.isArc && !seg.isRapid) {
          // Draw arc
          const cx = toCanvasX(seg.start.x + seg.arcCenter.i);
          const cy = toCanvasY(seg.start.y + seg.arcCenter.j);
          const radius = Math.sqrt(
            Math.pow(seg.arcCenter.i * scaleX, 2) + 
            Math.pow(seg.arcCenter.j * scaleY, 2)
          );
          const startAngle = Math.atan2(y1 - cy, x1 - cx);
          const endAngle = Math.atan2(y2 - cy, x2 - cx);
          
          ctx.arc(cx, cy, radius, startAngle, endAngle, seg.arcDir === 2);
        } else {
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
        }
      }
      
      ctx.stroke();
    }
    
    // Draw axis labels
    ctx.fillStyle = '#888';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    
    if (machineType === 'lathe') {
      ctx.fillText('Z', width / 2, height - 5);
      ctx.save();
      ctx.translate(10, height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('X', 0, 0);
      ctx.restore();
    } else {
      ctx.fillText('X', width / 2, height - 5);
      ctx.save();
      ctx.translate(10, height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('Y', 0, 0);
      ctx.restore();
    }
    
    // Draw scale indicator
    ctx.fillStyle = '#666';
    ctx.font = '9px monospace';
    ctx.textAlign = 'right';
    const scaleText = `Scale: 1" = ${(scaleX).toFixed(0)}px`;
    ctx.fillText(scaleText, width - 10, height - 5);
    
  }, [toolpath, width, height, machineType, showRapids, showGrid, showWorkpiece, stockDimensions]);
  
  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="rounded-lg border border-industrial-border"
        data-testid="toolpath-canvas"
      />
      
      {/* Legend */}
      <div className="absolute bottom-2 left-2 flex gap-3 text-[10px] bg-black/50 px-2 py-1 rounded">
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-green-500"></div>
          <span className="text-green-400">Cut</span>
        </div>
        {showRapids && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-orange-500" style={{ borderTop: '1px dashed' }}></div>
            <span className="text-orange-400">Rapid</span>
          </div>
        )}
        {showWorkpiece && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-500/30 border border-gray-500"></div>
            <span className="text-gray-400">Stock</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolpathViewer;
